import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  BoardLead,
  BoardLeadsResponse,
  LeadStage,
  PipelineStage,
  StageLeadCount,
} from "@school-sales-ai/shared";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { buildQueryString } from "@/lib/query-string";
import { isConversionStage } from "@/lib/board-drag";
import { celebrateConversion } from "@/lib/celebrate";
import { usePipelineStagesQuery } from "@/hooks/usePipelineStages";
import { useCatalogContext } from "@/hooks/useCatalog";
import { useCrmStore } from "@/stores/crm.store";

type BoardResponse = BoardLeadsResponse;

/**
 * How many cards each column loads up front.
 *
 * ~7 fit on screen, so this is about two screenfuls of scroll before the column
 * asks for more. Sized against the pilot's real board: at 15 the flat view mounts
 * ~111 cards instead of 478. Since columns are ordered by score, what is deferred
 * is the cold tail — the top N are the N worth working.
 */
export const PER_STAGE_LIMIT = 15;

/**
 * Ceiling for a single column, mirroring the server's `perStageLimit` max in
 * `crm.routes.ts`. Past it the column stops offering "Ver más" and points at the
 * Tabla instead — asking for more would just 400.
 */
export const MAX_STAGE_LIMIT = 200;

export type GroupedLeads = Record<LeadStage, BoardLead[]>;

// Exported for unit testing. Buckets leads by the given (dynamic) column keys.
export function groupByStage(items: BoardLead[], stageKeys: string[]): GroupedLeads {
  const grouped = Object.fromEntries(
    stageKeys.map((s) => [s, [] as BoardLead[]]),
  ) as GroupedLeads;
  for (const lead of items) {
    // Delete-if-empty guarantees a lead's column still exists, but guard anyway
    // so a stale key never crashes the board.
    grouped[lead.stage]?.push(lead);
  }
  for (const key of stageKeys) {
    grouped[key]!.sort((a, b) => b.score - a.score);
  }
  return grouped;
}

// Buckets leads into a 2D grid: one GroupedLeads per lane. `laneKeys` are cycle
// UUIDs; leads with no cycle go into the "none" lane (always present). Exported
// for unit testing.
export function groupByCycleAndStage(
  items: BoardLead[],
  stageKeys: string[],
  laneKeys: string[],
): Record<string, GroupedLeads> {
  const allLanes = [...laneKeys, "none"];
  const byLane: Record<string, BoardLead[]> = Object.fromEntries(
    allLanes.map((k) => [k, [] as BoardLead[]]),
  );
  for (const lead of items) {
    const lane = lead.cycleId ?? "none";
    // A lead whose cycle isn't among the active lanes still shows under "none"
    // so it's never dropped from the board.
    (byLane[lane] ?? byLane.none!).push(lead);
  }
  return Object.fromEntries(
    allLanes.map((k) => [k, groupByStage(byLane[k]!, stageKeys)]),
  );
}

/**
 * The filters the board sends to the server.
 *
 * `stages` is deliberately absent: the board renders one column per stage, so a
 * stage filter is applied client-side by hiding the other columns (see
 * `visibleStages` in BoardView). Filtering it server-side would also zero out
 * the per-column counts ManageColumnsModal uses to block deleting a column that
 * still holds leads.
 *
 * Exported shape is also the pipeline query key, so `useMoveCard` can target the
 * exact cache entry `usePipelineLeads` populated.
 */
/**
 * Whether the board is in cycle-swimlane mode: exactly ONE product is selected
 * and it schedules by cycles. With several, the lanes would mix calendars and
 * the "everything fits unpaginated" assumption below no longer holds, so the
 * board stays plain. Single source of truth — the request needs it (lanes are
 * not paginated) and so does BoardView (it draws the lanes).
 */
export function useSwimlaneMode(): boolean {
  const productIds = useCrmStore((s) => s.productIds);
  const { data: catalog } = useCatalogContext();
  if (productIds.length !== 1) return false;
  return (
    catalog?.products.find((p) => p.id === productIds[0])?.scheduleType === "cycles"
  );
}

export function useBoardFilters(): Record<string, string | number> {
  const swimlanes = useSwimlaneMode();
  const channel = useCrmStore((s) => s.channel);
  const scoreMin = useCrmStore((s) => s.scoreMin);
  const scoreMax = useCrmStore((s) => s.scoreMax);
  const productIds = useCrmStore((s) => s.productIds);
  const cycleIds = useCrmStore((s) => s.cycleIds);
  const search = useCrmStore((s) => s.search);
  const from = useCrmStore((s) => s.from);
  const to = useCrmStore((s) => s.to);
  const labelIds = useCrmStore((s) => s.labelIds);
  const assigneeIds = useCrmStore((s) => s.assigneeIds);

  // Lists are serialized as comma-joined strings so this stays a flat
  // Record<string,string> (the query key and the querystring both consume it).
  const productIdsKey = productIds.join(",");
  const cycleIdsKey = cycleIds.join(",");
  const labelIdsKey = labelIds.join(",");
  const assigneeIdsKey = assigneeIds.join(",");
  // Swimlane mode asks for no per-column cap: it is always scoped to one product,
  // so the whole set is small, and a per-column page would truncate each lane
  // wrongly (the cap is per stage, not per lane+stage). "" is dropped from the
  // querystring by buildQueryString.
  const perStageLimit = swimlanes ? "" : PER_STAGE_LIMIT;
  return useMemo(
    () => ({
      channel, scoreMin, scoreMax,
      productIds: productIdsKey,
      cycleIds: cycleIdsKey,
      search, from, to,
      labelIds: labelIdsKey,
      assigneeIds: assigneeIdsKey,
      perStageLimit,
    }),
    [channel, scoreMin, scoreMax, productIdsKey, cycleIdsKey, search, from, to, labelIdsKey, assigneeIdsKey, perStageLimit],
  );
}

export function usePipelineLeads({ enabled = true }: { enabled?: boolean } = {}): {
  stages: PipelineStage[];
  grouped: GroupedLeads;
  counts: Record<string, number>;
  truncated: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const filters = useBoardFilters();
  const stagesQuery = usePipelineStagesQuery();
  const leadsQuery = useQuery({
    queryKey: ["pipeline", filters],
    queryFn: () => {
      const qs = buildQueryString(filters);
      return api.get<BoardResponse>(`/crm/leads/board${qs ? `?${qs}` : ""}`);
    },
    enabled,
    // Each filter combination mints its own cache entry holding the whole board.
    // With the default 5-minute gcTime, a few minutes of typing in the search box
    // retains a dozen ~500-lead arrays that nothing will ever read again.
    gcTime: 60_000,
  });

  const stages = useMemo(
    () => stagesQuery.data ?? [],
    [stagesQuery.data],
  );
  const grouped = useMemo(
    () => groupByStage(leadsQuery.data?.items ?? [], stages.map((s) => s.key)),
    [leadsQuery.data, stages],
  );

  // Server-side totals per stage. The board only loads the top slice of each
  // column, so this — never `grouped[key].length` — is what a badge may show.
  const counts = useMemo(
    () =>
      Object.fromEntries(
        (leadsQuery.data?.counts ?? []).map((c: StageLeadCount) => [c.stage, c.count]),
      ),
    [leadsQuery.data],
  );

  return {
    stages,
    grouped,
    counts,
    truncated: leadsQuery.data?.truncated ?? false,
    isLoading:
      (enabled && leadsQuery.isLoading) || stagesQuery.isLoading,
    isError: leadsQuery.isError || stagesQuery.isError,
  };
}

/**
 * Extra cards for one expanded column.
 *
 * Additive on purpose: the main board query keeps its single cache entry (so
 * `useMoveCard`'s optimistic update is untouched) and an expanded column gets its
 * own entry alongside. Growing `limit` re-asks for "the first N" rather than
 * appending from a cursor — under `score DESC, id` that is always a consistent
 * superset, which append-with-cursor is not while drag & drop keeps moving cards
 * between stages.
 */
export function useStageOverflow(
  stageKey: string,
  limit: number | undefined,
): BoardLead[] | undefined {
  const filters = useBoardFilters();
  const query = useQuery({
    queryKey: ["pipeline-stage", stageKey, filters, limit],
    queryFn: () => {
      const qs = buildQueryString({
        ...filters,
        stages: stageKey,
        perStageLimit: limit ?? PER_STAGE_LIMIT,
      });
      return api.get<BoardResponse>(`/crm/leads/board?${qs}`);
    },
    enabled: limit !== undefined,
    gcTime: 60_000,
  });
  return query.data?.items;
}

type MoveCardInput = { leadId: string; toStage: LeadStage };

export function useMoveCard(): ReturnType<
  typeof useMutation<{ id: string; stage: LeadStage }, Error, MoveCardInput>
> {
  const queryClient = useQueryClient();
  // Must match the active pipeline query key so optimistic get/set target the
  // same cache entry (exact-key operations).
  const pipelineKey = ["pipeline", useBoardFilters()] as const;
  // Read here rather than at each call site: the board moves a card from three
  // places (desktop drag, the reason modal's confirm, the mobile "Avanzar" button)
  // and all three go through this one mutation.
  const { data: stages } = usePipelineStagesQuery();

  return useMutation({
    mutationFn: ({ leadId, toStage }: MoveCardInput) =>
      api.patch<{ id: string; stage: LeadStage }>(
        `/crm/leads/${leadId}/stage`,
        { stage: toStage },
      ),
    onMutate: async ({ leadId, toStage }) => {
      await queryClient.cancelQueries({ queryKey: pipelineKey });
      const snapshot = queryClient.getQueryData<BoardResponse>(pipelineKey);

      queryClient.setQueryData<BoardResponse>(pipelineKey, (old) => {
        if (!old) return old;
        const updated = old.items.map((l) =>
          l.id === leadId ? { ...l, stage: toStage } : l,
        );
        return { ...old, items: updated };
      });

      return { snapshot };
    },
    // Celebrating happens only once the server has confirmed the move. Firing it in
    // `onMutate` alongside the optimistic update would throw confetti for a move
    // that is about to roll back.
    onSuccess: (_data, { toStage }) => {
      if (isConversionStage(toStage, stages)) celebrateConversion();
    },
    onError: (_err, _vars, context) => {
      const ctx = context as { snapshot?: BoardResponse } | undefined;
      if (ctx?.snapshot) {
        queryClient.setQueryData(pipelineKey, ctx.snapshot);
      }
      toast.error("Error al mover lead");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline-stage"] });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["product-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["cycle-counts"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-stats"] });
    },
  });
}
